import {
    COLLAPSED_SAME_BUSINESS_ROW_TOOLTIP,
    MERGE_ACCOUNT_SINGLE_ENTITY_TOOLTIP,
    REASSIGN_ROLE_OUTCOME_TOOLTIP,
    mergeActionTooltip,
    REASSIGN_ROLE_SINGLE_ENTITY_TOOLTIP,
} from "@components/accounting/chartOfAccounts/constants";
import { getAccountRoleReassignmentBlockMessage } from "@common/constants/domains/accounting/accountRole";
import { AccountRole } from "@common/types/domains/accounting/account";
import {
    EditorRowCapabilities,
    GetRowCapabilities,
} from "../editorCapabilities";
import { NestedEditorRow } from "./nestedRowTypes";
import {
    groupNestedRowRefusals,
    NestedEditorHost,
    NestedRowGesture,
    NestedRowRefusalGroup,
    resolveNestedRowRefusal,
} from "./nestedRowRefusal";

export type NestedAccountMenuItemState =
    | { kind: "hidden" }
    | {
          kind: "disabled";
          tooltip?: string;
          // Rendered instead of `tooltip`: the reasons the row is refused, each
          // with the businesses holding it. Kept as data so this module stays
          // free of the entity lookup the tooltip needs.
          refusalGroups?: NestedRowRefusalGroup[];
      }
    | { kind: "enabled"; tooltip?: string };

export interface NestedAccountMenuItems {
    archive: NestedAccountMenuItemState;
    merge: NestedAccountMenuItemState;
    reassignRole: NestedAccountMenuItemState;
    removeOpeningBalance: NestedAccountMenuItemState;
    delete: NestedAccountMenuItemState;
}

interface ResolveNestedAccountMenuItemsParams {
    row: NestedEditorRow;
    host: NestedEditorHost;
    isWriting: boolean;
    capabilities: EditorRowCapabilities;
    isAccountArchived: boolean;
    getConstituentCapabilities: GetRowCapabilities;
}

interface RefusalStateParams extends Pick<
    ResolveNestedAccountMenuItemsParams,
    "row" | "getConstituentCapabilities"
> {
    gesture: NestedRowGesture;
}

const HIDDEN: NestedAccountMenuItemState = { kind: "hidden" };
const ENABLED: NestedAccountMenuItemState = { kind: "enabled" };

const refusalState = ({
    row,
    gesture,
    getConstituentCapabilities,
}: RefusalStateParams): NestedAccountMenuItemState | null => {
    const refusals = resolveNestedRowRefusal({
        row,
        gesture,
        getRowCapabilities: getConstituentCapabilities,
    });
    if (refusals.length === 0) {
        return null;
    }
    return {
        kind: "disabled",
        refusalGroups: groupNestedRowRefusals(refusals),
    };
};

// Workspace Enable ignores archiveBlockReason so a linked/system-role row can still be turned back on.
const resolveArchive = ({
    row,
    host,
    isAccountArchived,
    getConstituentCapabilities,
}: ResolveNestedAccountMenuItemsParams): NestedAccountMenuItemState => {
    if (host === "workspace" && isAccountArchived) {
        return ENABLED;
    }
    return (
        refusalState({
            row,
            gesture: "archive",
            getConstituentCapabilities,
        }) ?? ENABLED
    );
};

const resolveDelete = ({
    row,
    getConstituentCapabilities,
}: ResolveNestedAccountMenuItemsParams): NestedAccountMenuItemState =>
    refusalState({
        row,
        gesture: "delete",
        getConstituentCapabilities,
    }) ?? ENABLED;

const resolveMerge = ({
    row,
    host,
    isWriting,
    capabilities,
    getConstituentCapabilities,
}: ResolveNestedAccountMenuItemsParams): NestedAccountMenuItemState => {
    if (
        host !== "workspace" ||
        row.kind !== "account" ||
        !row.account ||
        !capabilities.hasChartSeed
    ) {
        return HIDDEN;
    }
    const refusal = refusalState({
        row,
        gesture: "merge",
        getConstituentCapabilities,
    });
    if (refusal) {
        return refusal;
    }
    const uniqueEntityCount = new Set(
        row.accounts.map((account) => account.entityId),
    ).size;
    if (uniqueEntityCount > 1) {
        return {
            kind: "disabled",
            tooltip: MERGE_ACCOUNT_SINGLE_ENTITY_TOOLTIP,
        };
    }
    if (row.accounts.length > 1) {
        return {
            kind: "disabled",
            tooltip: COLLAPSED_SAME_BUSINESS_ROW_TOOLTIP,
        };
    }
    if (isWriting) {
        return { kind: "disabled" };
    }
    const role = row.accounts.reduce<AccountRole | null>(
        (resolvedRole, account) =>
            resolvedRole ?? getConstituentCapabilities(account).role,
        null,
    );
    return { kind: "enabled", tooltip: mergeActionTooltip(role) };
};

// No dropdown means no ambiguity about which same-business balance is meant, so
// a collapsed two-balance row stays enabled and clears both on confirm.
const resolveRemoveOpeningBalance = ({
    host,
    isWriting,
    capabilities,
}: ResolveNestedAccountMenuItemsParams): NestedAccountMenuItemState => {
    if (host !== "workspace" || !capabilities.hasOpeningBalance) {
        return HIDDEN;
    }
    if (isWriting) {
        return { kind: "disabled" };
    }
    return ENABLED;
};

// Visibility walks constituents (not the OR'd representative): a role-less
// representative in front of a holder still offers the item. Then it takes
// Merge's single-business / collapsed-row disablement. A template has no merge
// to point Receivables and Payables at, so the block is workspace-only.
const resolveReassignRole = ({
    row,
    host,
    isWriting,
    getConstituentCapabilities,
}: ResolveNestedAccountMenuItemsParams): NestedAccountMenuItemState => {
    const isRoleHolder = row.accounts.some(
        (account) => getConstituentCapabilities(account).role != null,
    );
    if (!isRoleHolder) {
        return HIDDEN;
    }
    const blockMessage =
        host === "workspace"
            ? row.accounts.reduce<string | null>(
                  (message, account) =>
                      message ??
                      getAccountRoleReassignmentBlockMessage(
                          getConstituentCapabilities(account).role,
                      ),
                  null,
              )
            : null;
    if (blockMessage !== null) {
        return {
            kind: "disabled",
            tooltip: blockMessage,
        };
    }
    const uniqueEntityCount = new Set(
        row.accounts.map((account) => account.entityId),
    ).size;
    if (uniqueEntityCount > 1) {
        return {
            kind: "disabled",
            tooltip: REASSIGN_ROLE_SINGLE_ENTITY_TOOLTIP,
        };
    }
    if (row.accounts.length > 1) {
        return {
            kind: "disabled",
            tooltip: COLLAPSED_SAME_BUSINESS_ROW_TOOLTIP,
        };
    }
    if (isWriting) {
        return { kind: "disabled" };
    }
    return { kind: "enabled", tooltip: REASSIGN_ROLE_OUTCOME_TOOLTIP };
};

export const resolveNestedAccountMenuItems = (
    params: ResolveNestedAccountMenuItemsParams,
): NestedAccountMenuItems => ({
    archive: resolveArchive(params),
    merge: resolveMerge(params),
    reassignRole: resolveReassignRole(params),
    removeOpeningBalance: resolveRemoveOpeningBalance(params),
    delete: resolveDelete(params),
});
